import { useFormik } from "formik";
import * as Yup from "yup";
import { format, isBefore } from "date-fns";
import { AutoCompleteItem } from "../../autocomplete/interface";
import {
  FirmaRelationKode,
  FirmaRelationKodeDict,
} from "../OpretPerson/useOpretPerson";
import { validationMessages } from "../../../constants/validation";
import { PersonDto, PersonFirma } from "../../../interfaces/schema";
import { useMutation } from "@tanstack/react-query";
import { patch, post } from "../../../api/util";
import { getPatchOperations } from "../../../util/form";
import {
  cprLoebeNrSchema,
  stripCprRestrictedChanges,
} from "../../../util/cprAccess";
import { dateIsWithinInterval, stripTime } from "../../../util/date";
import { useState } from "react";
import { logSafeError } from "../../../util/logSafeError";

export function useFuldmaegtigAutorisation(
  person: PersonDto,
  currentFirma: PersonFirma | undefined,
  cpr: string,
  canReadCpr: boolean,
) {
  const {
    mutateAsync: patchPersonMutation,
    isPending: isPatchLoading,
    error: patchError,
    data: patchData,
    reset: resetPersonQuery,
  } = useMutation({
    mutationFn: async (body: {
      foedselsDato?: string;
      CprLoebeNr?: string;
    }) => {
      return await patch({
        query: `person/${person.id}`,
        body: JSON.stringify(getPatchOperations(body)),
      });
    },
  });
  const {
    mutateAsync,
    isPending: isLoading,
    error,
    data: postData,
  } = useMutation({
    mutationFn: async (body: AutoriserFuldmaegtigDTO) => {
      return await post({
        query: `person/${person.id}/wizards/autoriserfuldmaegtig`,
        body,
      });
    },
  });
  const [patchAttempted, setPatchAttempted] = useState(false);

  const initialValues = {
    foedselsDato: person.foedselsDato
      ? new Date(person.foedselsDato)
      : undefined,
    cprNr: cpr ? cpr.split("-")[1] : "",
    principal: undefined,
    startDato: undefined,
    firmaStartDato: undefined,
    harFirmaRelation: true,
    hasCurrentFirma: !!currentFirma,
    firma: undefined,
    firmaRelation: FirmaRelationKodeDict.ansat,
    emailArbejde: "",
    sammeFirma: 1,
    primaer: false,
  };
  const formik = useFormik<FuldmaegtigFormik>({
    initialValues,
    validateOnMount: true,
    validationSchema: Yup.object({
      foedselsDato: Yup.date()
        .nullable()
        .required("Fødselsdato skal være sat")
        .test("foedselsDato", "Årstal skal være mindst 1900", (item) => {
          if (!item) return true;
          return item.getFullYear() > 1899;
        })
        .test("foedselsDato", "Må ikke være i fremtiden", (item) => {
          if (!item) return true;
          const date = new Date(item);
          return isBefore(date, new Date());
        }),
      cprNr: cprLoebeNrSchema(canReadCpr),
      principal: Yup.object()
        .shape({
          id: Yup.string().required(validationMessages.required),
        })
        .required(validationMessages.required),
      firma: Yup.object()
        .nullable()
        .when("hasCurrentFirma", {
          is: false,
          then: (schema) => schema.required("Firma skal være sat"),
          otherwise: (schema) =>
            schema.when("sammeFirma", {
              is: (value) => value === 2 || value === 3,
              then: (schema) => schema.required("Firma skal være sat"),
            }),
        }),
      emailArbejde: Yup.string().email("Email har forkert format"),
      startDato: Yup.date().nullable().required(validationMessages.required),
      firmaStartDato: Yup.date()
        .nullable()
        .when("sammeFirma", {
          is: () => !currentFirma,
          then: (schema) => schema.required(validationMessages.required),
        })
        .when("sammeFirma", {
          is: (sammeFirma: number) => sammeFirma === 1 && !!currentFirma,
          then: (schema) => schema.nullable(),
          otherwise: (schema) =>
            schema
              .when("sammeFirma", {
                is: (sammeFirma: number) => sammeFirma > 1,
                then: (schema) => schema.required(validationMessages.required),
              })
              .test(
                "relationConsistency",
                "En Advokatfuldmægtig skal have en ansat eller partner FirmaRelation for ethvert fremtidigt tidspunkt",
                function () {
                  const { firmaStartDato, startDato }: FuldmaegtigFormik =
                    this.parent;
                  if (startDato && firmaStartDato) {
                    if (!currentFirma) {
                      return stripTime(startDato) >= stripTime(firmaStartDato);
                    }
                  }
                  return true;
                },
              ),
        }),
    }),
    onSubmit: async (values) => {
      const {
        foedselsDato,
        cprNr,
        principal,
        startDato,
        firmaRelation,
        firma,
        emailArbejde,
        firmaStartDato,
        sammeFirma,
        primaer,
      } = values;

      try {
        const formattedNewDate = foedselsDato
          ? format(foedselsDato, "yyyy-MM-dd")
          : null;
        const formattedOriginalDate = initialValues.foedselsDato
          ? format(initialValues.foedselsDato, "yyyy-MM-dd")
          : null;
        if (
          (!!formattedNewDate && formattedNewDate !== formattedOriginalDate) ||
          cprNr !== initialValues.cprNr
        ) {
          // ADV-349: uden CprRead sendes CprLoebeNr aldrig, og fødselsdatoen ikke for en
          // person med CPR-nummer — backenden ville afvise PATCH'en.
          const patchBody = stripCprRestrictedChanges(
            {
              foedselsDato: foedselsDato
                ? format(foedselsDato, "yyyy-MM-dd")
                : "",
              CprLoebeNr: cprNr,
            },
            {
              canReadCpr,
              harCprNummer: !!person.harCprNummer,
              foedselsDato: person.foedselsDato,
            },
          );
          if (Object.keys(patchBody).length > 0) {
            setPatchAttempted(true);
            await patchPersonMutation(patchBody);
          }
        }

        const formattedStartDato = startDato
          ? format(startDato, "yyyy-MM-dd")
          : "";
        const formattedFirmaStartDato = firmaStartDato
          ? format(firmaStartDato, "yyyy-MM-dd")
          : "";

        const dtoGlobals = {
          principalPersonId: principal?.id ?? null,
          fraDato: formattedStartDato,
        };

        const nyPersonFirmarelation: NyFirmaRelation = {
          emailArbejde,
          firmaId: firma?.id ?? null,
          periodeFraDato: formattedFirmaStartDato,
          personfirmatypeNr: firmaRelation.kode,
          bemaerkninger: null,
          priorieteretPrimaerFra:
            primaer && sammeFirma === 2 ? formattedFirmaStartDato : null,
        };
        const currentFirmas: PersonFirma[] =
          (startDato &&
            person.personFirma?.filter((pf) =>
              dateIsWithinInterval(
                startDato,
                new Date(pf.periode.fraDato),
                new Date(pf.periode.tilDatoInklusiv),
              ),
            )) ??
          [];
        const futureFirmaRelationer =
          startDato && person.personFirma
            ? person.personFirma.filter(
                (element) => new Date(element?.periode.fraDato) > startDato,
              )
            : [];
        const mutationData: AutoriserFuldmaegtigDTO = {
          ...dtoGlobals,
          nyPersonFirmarelation:
            sammeFirma === 1 && currentFirmas.length > 0
              ? null
              : nyPersonFirmarelation,
          fortsaettendePersonFirmarelationer: futureFirmaRelationer.map(
            (fr) => fr.id,
          ),
          ophoerendePersonFirmarelationer: [],
        };

        if (currentFirmas.length > 0) {
          if (sammeFirma === 1 || sammeFirma === 2) {
            for (let index = 0; index < currentFirmas.length; index++) {
              const currentFirma = currentFirmas[index];
              mutationData.fortsaettendePersonFirmarelationer.push(
                currentFirma.id,
              );
            }
          }
          if (sammeFirma === 3) {
            for (let index = 0; index < currentFirmas.length; index++) {
              const currentFirma = currentFirmas[index];
              mutationData.ophoerendePersonFirmarelationer.push(
                currentFirma.id,
              );
            }
          }
        }

        await mutateAsync(mutationData);
      } catch (error) {
        logSafeError(error);
      }
    },
  });
  const dato = formik.values.startDato;
  const aktiveFirmaRelationer =
    person.personFirma?.filter((pf) =>
      dato
        ? dateIsWithinInterval(
            dato,
            new Date(pf.periode.fraDato),
            new Date(pf.periode.tilDatoInklusiv),
          )
        : false,
    ) ?? [];
  const futureFirmaRelationer =
    dato && person.personFirma
      ? person.personFirma.filter(
          (element) => new Date(element?.periode.fraDato) > dato,
        )
      : [];
  return {
    formik,
    isLoading,
    postError: error,
    patchError,
    isPatchLoading,
    postData,
    patchData,
    patchAttempted,
    resetPersonQuery,
    aktiveFirmaRelationer,
    futureFirmaRelationer,
  };
}

export type FuldmaegtigFormik = {
  foedselsDato: Date | undefined;
  cprNr: string;
  principal: AutoCompleteItem | undefined;
  harFirmaRelation: boolean;
  firma: AutoCompleteItem | undefined;
  firmaRelation: FirmaRelationKode;
  startDato: Date | undefined;
  firmaStartDato: Date | undefined;
  emailArbejde: string;
  sammeFirma: number;
  hasCurrentFirma: boolean;
  primaer: boolean;
};

interface NyFirmaRelation {
  personfirmatypeNr: "Ansat" | "Partner" | "Ejer" | "Stifter" | "AnsatOgsaaI";
  periodeFraDato: string;
  periodeTilDatoInklusiv?: string;
  bemaerkninger?: string | null;
  priorieteretPrimaerFra?: string | null;
  fakturaReference?: string | null;
  emailArbejde: string;
  firmaId: string | null;
}
interface AutoriserFuldmaegtigDTO {
  fraDato: string;
  principalPersonId: string | null;
  ophoerendePersonFirmarelationer: string[];
  fortsaettendePersonFirmarelationer: string[];
  nyPersonFirmarelation: NyFirmaRelation | null;
}
